import { useEffect, useState } from "react";
import { CipExemptBanner } from "../components/CipExemptBanner";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { logError } from "../lib/logError";
import { PageHeader } from "../components/ui-helpers";
import { useModuleVisibility } from "../hooks/useModuleVisibility";
import { AnnouncementsBanner } from "../components/AnnouncementsBanner";
import StandardsCoverageSummary from "../components/StandardsCoverageSummary";
import WhatsNew from "../components/WhatsNew";
import { WarningOctagon, CheckSquare, CalendarBlank, ArrowSquareOut, ArrowClockwise, LockSimple, ArrowRight, Sparkle } from "@phosphor-icons/react";

const TASK_ICON = { star: CheckSquare, rapid_reviews: CheckSquare, documents: CheckSquare, incidents: WarningOctagon, scrm: WarningOctagon };

function TaskCard({ wf, nav }) {
  const Icon = TASK_ICON[wf.key] || CheckSquare;
  if (wf.state === "locked") {
    return (
      <button data-testid={`task-card-${wf.key}`} onClick={() => nav("/billing")}
        className="text-left border border-dashed border-amber-300 bg-amber-50/40 p-5 hover:bg-amber-50 transition-colors h-full">
        <div className="flex items-center gap-2 text-amber-700">
          <LockSimple weight="bold" size={18} />
          <div className="font-display font-bold text-base">{wf.label}</div>
        </div>
        <div className="mt-6 flex items-center gap-1.5 text-sm font-semibold text-amber-700">
          <Sparkle weight="fill" size={14} />
          <span data-testid={`task-upsell-${wf.key}`}>{wf.upsell || "Available with ClaroShield Light"}</span>
        </div>
        <div className="mt-1 text-xs text-amber-700/70">Tap to upgrade your plan →</div>
      </button>
    );
  }
  return (
    <div data-testid={`task-card-${wf.key}`} className="border border-border bg-card p-5 h-full flex flex-col">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon weight="bold" size={18} className="text-muted-foreground" />
          <div className="font-display font-bold text-base">{wf.label}</div>
        </div>
        <span data-testid={`task-count-${wf.key}`}
          className={`font-mono font-bold text-sm px-2 py-0.5 border ${wf.count > 0 ? "border-foreground text-foreground" : "border-border text-muted-foreground"}`}>{wf.count}</span>
      </div>
      <div className="mt-3 flex-1">
        {wf.tasks.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid={`task-empty-${wf.key}`}>No open tasks.</p>
        ) : (
          <ul className="space-y-2">
            {wf.tasks.map((t) => (
              <li key={t.tsk + t.route}>
                <button data-testid={`task-item-${t.tsk}`} onClick={() => nav(t.route)}
                  className="w-full text-left border-b border-border pb-2 hover:bg-secondary/40 -mx-1 px-1 transition-colors">
                  <div className="flex items-baseline gap-2">
                    <span className="font-mono text-[11px] font-bold text-muted-foreground shrink-0">{t.tsk}</span>
                    <span className="text-sm text-foreground truncate">{t.title}</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground font-mono ml-[52px] capitalize">{t.status}</div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <button data-testid={`task-viewall-${wf.key}`} onClick={() => nav(wf.route)}
        className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-foreground hover:gap-2 transition-all">
        {wf.count > wf.tasks.length ? `View all ${wf.count}` : "Open"} <ArrowRight size={14} />
      </button>
    </div>
  );
}

function TasksPanel({ nav }) {
  const [data, setData] = useState(null);
  const [mine, setMine] = useState(false);
  useEffect(() => {
    api.get(`/dashboard/tasks${mine ? "?mine=true" : ""}`).then((r) => setData(r.data)).catch((e) => logError("dashboard tasks load", e));
  }, [mine]);
  if (!data) return null;
  const visible = (data.workflows || []).filter((w) => w.state !== "hidden");
  const total = visible.filter((w) => w.state === "available").reduce((s, w) => s + w.count, 0);
  return (
    <div data-testid="tasks-panel">
      <div className="flex items-end justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="overline text-muted-foreground">TSK · Tasks</div>
          <h2 className="font-display font-black text-2xl tracking-tighter">Tasks</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            {total} {mine ? "assigned to you" : "open"} across your ClaroShield Light workflows.
          </p>
        </div>
        <div className="inline-flex border border-border" role="group" aria-label="Task assignment filter">
          <button data-testid="tasks-filter-all" onClick={() => setMine(false)} aria-pressed={!mine}
            className={`px-3 py-1.5 text-xs font-semibold transition-colors ${!mine ? "bg-foreground text-background" : "bg-transparent text-muted-foreground hover:bg-secondary"}`}>All tasks</button>
          <button data-testid="tasks-filter-mine" onClick={() => setMine(true)} aria-pressed={mine}
            className={`px-3 py-1.5 text-xs font-semibold border-l border-border transition-colors ${mine ? "bg-foreground text-background" : "bg-transparent text-muted-foreground hover:bg-secondary"}`}>Assigned to me</button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {visible.map((wf) => <TaskCard key={wf.key} wf={wf} nav={nav} />)}
      </div>
    </div>
  );
}

function Stat({ label, value, accent, testid, onClick, sub }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className="text-left border border-border bg-card p-5 hover:bg-secondary/50 transition-colors h-full">
      <div className="overline text-muted-foreground">{label}</div>
      <div className={`font-display font-black text-4xl tracking-tighter mt-2 ${accent || ""}`}>{value}</div>
      {sub ? <div className="text-xs text-muted-foreground mt-1">{sub}</div> : null}
    </button>
  );
}

function ScopeTile({ label, value, tone, hint, testid }) {
  return (
    <div className="bg-card p-4" data-testid={testid}>
      <div className="overline text-muted-foreground">{label}</div>
      <div className={`font-display font-black text-3xl tracking-tighter mt-1 ${tone || ""}`}>{value}</div>
      <div className="text-[11px] text-muted-foreground mt-0.5">{hint}</div>
    </div>
  );
}

const MEETINGS_PREF_KEY = "claroshield.meetings.codes";

function RegionalMeetings({ nav }) {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null); // null until initialized from server/localStorage
  const [busy, setBusy] = useState(false);
  const [nonce, setNonce] = useState(0);

  // Fetch meetings for a given selection (or default when codes is null).
  useEffect(() => {
    let tries = 0, timer, cancelled = false;
    const load = (codes) => {
      const qs = codes && codes.length ? `?codes=${codes.join(",")}` : "";
      return api.get(`/dashboard/regional-meetings${qs}`).then((r) => {
        if (cancelled) return;
        setData(r.data);
        if (selected === null) {
          // First load: prefer saved preference, else server default.
          const saved = (() => { try { return JSON.parse(localStorage.getItem(MEETINGS_PREF_KEY)); } catch { return null; } })();
          setSelected(Array.isArray(saved) && saved.length ? saved : (r.data.selected || r.data.default_selected || []));
        }
        if (r.data.refreshing && tries < 6) { tries += 1; timer = setTimeout(() => load(codes), 12000); }
      }).catch(() => { if (cancelled) return; setData((prev) => prev || { all_sources: [], sources: [], meetings: [] }); });
    };
    load(selected);
    return () => { cancelled = true; timer && clearTimeout(timer); };
  }, [selected, nonce]);

  const toggle = (code) => {
    setSelected((prev) => {
      const cur = prev || [];
      const next = cur.includes(code) ? cur.filter((c) => c !== code) : [...cur, code];
      try { localStorage.setItem(MEETINGS_PREF_KEY, JSON.stringify(next)); } catch (e) { logError("meetings pref persist", e); }
      return next;
    });
  };

  const refreshNow = async () => {
    const sel = selected || [];
    if (!sel.length || busy) return;
    setBusy(true);
    try {
      await api.post(`/dashboard/regional-meetings/refresh?codes=${sel.join(",")}`);
      setNonce((n) => n + 1); // re-run the loader, which will poll while sources refresh
    } catch (e) { logError("regional meetings refresh", e); }
    finally { setBusy(false); }
  };

  if (!data) return null;
  const allSources = data.all_sources || [];
  const sel = selected || data.selected || [];
  const meetings = (data.meetings || []).slice(0, 40);
  return (
    <div className="border border-border bg-card" data-testid="regional-meetings">
      <div className="flex flex-col gap-3 border-b border-border px-5 py-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <CalendarBlank size={18} />
            <div>
              <div className="overline text-muted-foreground">Regional Meetings &amp; Updates</div>
              <div className="text-sm text-muted-foreground">Toggle NERC and any Regional Entity to include their upcoming events.</div>
            </div>
          </div>
          <button type="button" onClick={refreshNow} disabled={busy || sel.length === 0}
            data-testid="meetings-refresh-btn" title="Fetch the latest events now"
            className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border px-2.5 py-1.5 hover:bg-secondary disabled:opacity-50 disabled:cursor-not-allowed">
            <ArrowClockwise size={14} className={busy || data.refreshing ? "animate-spin" : ""} />
            {busy || data.refreshing ? "Refreshing…" : "Refresh now"}
          </button>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="meeting-region-toggles">
          {allSources.map((s) => {
            const on = sel.includes(s.code);
            return (
              <div key={s.code} className="inline-flex items-stretch">
                <button type="button" onClick={() => toggle(s.code)} data-testid={`meeting-toggle-${s.code}`}
                  aria-pressed={on}
                  className={`inline-flex items-center gap-1.5 text-xs font-semibold border px-2.5 py-1 transition-colors ${on ? "bg-foreground text-background border-foreground" : "bg-transparent text-muted-foreground border-border hover:bg-secondary"}`}>
                  <span className={`inline-block w-2 h-2 rounded-full ${on ? "bg-background" : "bg-muted-foreground/40"}`} />
                  {s.name}
                </button>
                <a href={s.url} target="_blank" rel="noreferrer" data-testid={`meeting-source-${s.code}`}
                  title={`Open ${s.name} calendar`}
                  className="inline-flex items-center border border-l-0 border-border px-1.5 hover:bg-secondary text-muted-foreground">
                  <ArrowSquareOut size={12} />
                </a>
              </div>
            );
          })}
        </div>
      </div>
      <button type="button" data-testid="meetings-sdp-link" onClick={() => nav("/sdp")}
        className="flex w-full items-center justify-between gap-2 border-b border-border bg-secondary/40 px-5 py-2.5 text-left hover:bg-secondary transition-colors">
        <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <ArrowClockwise size={15} /> Tracking a new or revised standard? Open the Standards Dev Process tracker
        </span>
        <ArrowRight size={15} className="text-muted-foreground shrink-0" />
      </button>
      <div className="max-h-[460px] overflow-auto" data-testid="regional-meetings-list">
        {meetings.length === 0 ? (
          <div className="px-5 py-6 text-sm text-muted-foreground" data-testid="meetings-empty">
            {sel.length === 0 ? "Select NERC or a Regional Entity above to view its upcoming events." : data.refreshing ? "Checking regional sites for upcoming meetings…" : "No upcoming meetings found. Use the source links above to view each entity's calendar."}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2">
            {meetings.map((m, i) => (
              <a key={`${m.region}-${m.title}-${i}`} href={m.url} target="_blank" rel="noreferrer"
                data-testid={`meeting-item-${i}`}
                className="flex items-start gap-3 px-5 py-3 border-b border-border md:odd:border-r hover:bg-secondary/50">
                <span className="shrink-0 text-[10px] font-bold uppercase border border-border px-1.5 py-0.5 mt-0.5">{m.region_name}</span>
                <span className="flex-1 min-w-0">
                  <span className="text-sm text-foreground block truncate">{m.title}</span>
                  {m.date && <span className="text-xs text-muted-foreground">{m.date}</span>}
                </span>
                <ArrowSquareOut size={13} className="text-muted-foreground mt-1 shrink-0" />
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const nav = useNavigate();
  const { isModuleHidden } = useModuleVisibility();

  useEffect(() => {
    api.get("/dashboard")
      .then((r) => setD(r.data))
      .catch((err) => { logError("Dashboard load failed", err); setLoadError(true); });
  }, []);

  if (loadError) return <div className="p-8 text-sm text-red-600" data-testid="dashboard-load-error">Couldn't load the dashboard. Please refresh the page.</div>;
  if (!d) return <div className="p-8 font-mono text-sm">Loading…</div>;
  const c = d.counts;
  const ss = d.scope_summary;

  return (
    <div>
      <PageHeader title="Compliance Dashboard" subtitle="Program governance, standards readiness, and compliance posture across your NERC requirement set" />
      <div className="p-8 space-y-8">
        <div className="flex justify-end -mb-4"><WhatsNew /></div>
        <CipExemptBanner />
        <StandardsCoverageSummary />
        {ss?.plan_locked && (
          <div data-testid="scope-summary-strip" className="border border-border bg-card">
            <div className="flex items-center justify-between border-b border-border px-5 py-3">
              <div>
                <div className="overline text-muted-foreground">Your Registration Scope</div>
                <div className="text-sm text-muted-foreground">What your Category 2 Starter plan actively covers, of {ss.total} catalogued requirements.</div>
              </div>
              <button data-testid="scope-summary-view-btn" onClick={() => nav("/requirements")}
                className="border border-border px-3 py-1.5 text-sm font-semibold hover:bg-secondary">View requirements →</button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border">
              <ScopeTile testid="scope-active" label="Active" value={ss.active} tone="text-green-700" hint="full read &amp; write" />
              <ScopeTile testid="scope-readiness" label="NERC Readiness" value={ss.future} tone="text-blue-700" hint="future enforcement" />
              <ScopeTile testid="scope-retired" label="Retired" value={ss.historical} tone="text-zinc-500" hint="historical · read-only" />
              <ScopeTile testid="scope-locked" label="Outside scope" value={ss.locked} tone="text-amber-700" hint="upgrade to unlock" />
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-border border border-border">
          {!isModuleHidden("internal_controls") && <div className="bg-card"><Stat testid="stat-controls" label="Controls" value={c.controls} onClick={() => nav("/controls")} /></div>}
          {!isModuleHidden("incident_tracker") && <div className="bg-card"><Stat testid="stat-incidents" label="Incidents / Open PNCs" value={c.incidents_open ?? 0} accent="text-amber-600" onClick={() => nav("/incidents")} sub={
            <span>{`${c.pnc_open ?? 0} open PNC${(c.pnc_open ?? 0) === 1 ? "" : "s"}`}{((c.incidents_overdue ?? 0) + (c.pnc_overdue ?? 0)) > 0 && <span className="text-red-600 font-semibold"> · {(c.incidents_overdue ?? 0) + (c.pnc_overdue ?? 0)} overdue</span>}</span>
          } /></div>}
          {!isModuleHidden("evidence_repository") && <div className="bg-card"><Stat testid="stat-evidence" label="Evidence" value={c.evidence} onClick={() => nav("/evidence")} /></div>}
          {!isModuleHidden("documents") && <div className="bg-card"><Stat testid="stat-documents" label="Documents" value={c.documents} onClick={() => nav("/documents")} /></div>}
          <div className="bg-card"><Stat testid="stat-tasks" label="Open Tasks" value={c.tasks_open} onClick={() => nav("/tasks")} /></div>
          <div className="bg-card"><Stat testid="stat-coverage" label="Coverage" value={`${d.coverage_pct}%`} accent="text-[#16A34A]" onClick={() => nav("/requirements")} /></div>
        </div>

        <TasksPanel nav={nav} />

        <AnnouncementsBanner />

        <RegionalMeetings nav={nav} />
      </div>
    </div>
  );
}
